import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ContractorInvoiceView,
  CreateTimeEntryDto,
  DayPlan,
  DispatchBoard,
  DispatchSuggestion,
  OperationsStatus,
  RateCardView,
  TimeEntryStatus,
  TimeEntryView,
  TimeSuggestion,
  UpdateTimeEntryDto,
  UpsertRateCardDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';

/**
 * Contractor operations: dispatch, My day, time and the contractor's invoices.
 *
 * Every route is re-checked server-side — the permission at the scope in the
 * path, and the municipality having the module switched on. Nothing here is
 * offered unless `GET /operations/status` says the caller can use it.
 */
export const opsKeys = {
  all: ['operations'] as const,
  status: (userId: string) => ['operations', 'status', userId] as const,
  day: (date: string) => ['operations', 'day', date] as const,
  myTime: ['operations', 'me', 'time'] as const,
  suggestions: ['operations', 'me', 'suggestions'] as const,
  board: (lgId: string) => ['operations', 'dispatch', lgId] as const,
  dispatchSuggestions: (lgId: string, fileId: string) =>
    ['operations', 'dispatch', lgId, 'suggestions', fileId] as const,
  teamTime: (lgId: string, status: string) => ['operations', 'team-time', lgId, status] as const,
  received: (lgId: string) => ['operations', 'received', lgId] as const,
  receivedOne: (lgId: string, id: string) => ['operations', 'received', lgId, id] as const,
  rates: (companyId: string) => ['operations', 'rates', companyId] as const,
  invoices: (companyId: string) => ['operations', 'invoices', companyId] as const,
  invoice: (companyId: string, id: string) => ['operations', 'invoices', companyId, id] as const,
};

// ── Status ──────────────────────────────────────────────────────────────────

export async function getOperationsStatus(): Promise<OperationsStatus> {
  const { data } = await api.get<OperationsStatus>('/operations/status');
  return data;
}

export function useOperationsStatus(userId: string | null | undefined) {
  return useQuery({
    queryKey: opsKeys.status(userId ?? ''),
    queryFn: getOperationsStatus,
    enabled: !!userId,
    staleTime: 5 * 60_000,
  });
}

/** Whether a status offers anything at all — the nav group's visibility. */
export function operationsUsable(status: OperationsStatus | null | undefined): boolean {
  return !!status && (status.municipalities.length > 0 || status.companies.length > 0);
}

// ── My day ──────────────────────────────────────────────────────────────────

export interface DayQuery {
  date?: string;
  lat?: number;
  lng?: number;
}

export function useDayPlan(query: DayQuery, enabled = true) {
  return useQuery({
    queryKey: [...opsKeys.day(query.date ?? 'today'), query.lat ?? null, query.lng ?? null],
    queryFn: async () => {
      const { data } = await api.get<DayPlan>('/operations/me/day', { params: query });
      return data;
    },
    enabled,
  });
}

export function useSaveDayPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { date?: string; order: string[]; skipped: string[] }) => {
      const { data } = await api.put<DayPlan>('/operations/me/day', body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operations', 'day'] }),
  });
}

export function useOptimizeDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (query: DayQuery) => {
      const { data } = await api.post<DayPlan>('/operations/me/day/optimize', null, {
        params: query,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operations', 'day'] }),
  });
}

// ── My time ─────────────────────────────────────────────────────────────────

export function useMyTime() {
  return useQuery({
    queryKey: opsKeys.myTime,
    queryFn: async () => {
      const { data } = await api.get<TimeEntryView[]>('/operations/me/time');
      return data;
    },
  });
}

export function useTimeSuggestions() {
  return useQuery({
    queryKey: opsKeys.suggestions,
    queryFn: async () => {
      const { data } = await api.get<TimeSuggestion[]>('/operations/me/time/suggestions');
      return data;
    },
  });
}

function useInvalidateTime() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: opsKeys.myTime }),
      qc.invalidateQueries({ queryKey: opsKeys.suggestions }),
      qc.invalidateQueries({ queryKey: ['operations', 'team-time'] }),
    ]);
}

export function useRecordTime() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: async (body: CreateTimeEntryDto) => {
      const { data } = await api.post<TimeEntryView>('/operations/me/time', body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useEditTime() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateTimeEntryDto & { id: string }) => {
      const { data } = await api.patch<TimeEntryView>(`/operations/me/time/${id}`, body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteTime() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/operations/me/time/${id}`);
    },
    onSuccess: invalidate,
  });
}

// ── The municipality's side ────────────────────────────────────────────────

export function useDispatchBoard(lgId: string | undefined) {
  return useQuery({
    queryKey: opsKeys.board(lgId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<DispatchBoard>(
        `/operations/local-governments/${lgId}/dispatch`,
      );
      return data;
    },
    enabled: !!lgId,
  });
}

export function useDispatchSuggestions(lgId: string | undefined, fileId: string | null) {
  return useQuery({
    queryKey: opsKeys.dispatchSuggestions(lgId ?? '', fileId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<DispatchSuggestion[]>(
        `/operations/local-governments/${lgId}/dispatch/files/${fileId}/suggestions`,
      );
      return data;
    },
    enabled: !!lgId && !!fileId,
  });
}

export function useAssignFile(lgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ fileId, officerId }: { fileId: string; officerId: string }) => {
      const { data } = await api.post<{
        fileId: string;
        assignedOfficerId: string;
        assignedOfficerName: string;
      }>(`/operations/local-governments/${lgId}/dispatch/files/${fileId}/assign`, { officerId });
      return data;
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['operations', 'dispatch'] }),
        qc.invalidateQueries({ queryKey: ['files'] }),
      ]),
  });
}

export function useTeamTime(lgId: string | undefined, status: TimeEntryStatus | 'all') {
  return useQuery({
    queryKey: opsKeys.teamTime(lgId ?? '', status),
    queryFn: async () => {
      const { data } = await api.get<TimeEntryView[]>(
        `/operations/local-governments/${lgId}/time`,
        { params: status === 'all' ? undefined : { status } },
      );
      return data;
    },
    enabled: !!lgId,
  });
}

export function useReviewTime(lgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      ids: string[];
      decision: 'approve' | 'reject' | 'reopen';
      note?: string;
    }) => {
      const { data } = await api.post<TimeEntryView[]>(
        `/operations/local-governments/${lgId}/time/review`,
        body,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operations', 'team-time'] }),
  });
}

export function useReceivedInvoices(lgId: string | undefined) {
  return useQuery({
    queryKey: opsKeys.received(lgId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<ContractorInvoiceView[]>(
        `/operations/local-governments/${lgId}/invoices`,
      );
      return data;
    },
    enabled: !!lgId,
  });
}

export function useReceivedInvoice(lgId: string | undefined, id: string | null) {
  return useQuery({
    queryKey: opsKeys.receivedOne(lgId ?? '', id ?? ''),
    queryFn: async () => {
      const { data } = await api.get<ContractorInvoiceView>(
        `/operations/local-governments/${lgId}/invoices/${id}`,
      );
      return data;
    },
    enabled: !!lgId && !!id,
  });
}

// ── The contractor's books ─────────────────────────────────────────────────

export function useRateCards(companyId: string | undefined) {
  return useQuery({
    queryKey: opsKeys.rates(companyId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<RateCardView[]>(`/operations/companies/${companyId}/rates`);
      return data;
    },
    enabled: !!companyId,
  });
}

export function useSaveRateCard(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      localGovernmentId,
      ...body
    }: UpsertRateCardDto & { localGovernmentId: string }) => {
      const { data } = await api.put<RateCardView>(
        `/operations/companies/${companyId}/rates/${localGovernmentId}`,
        body,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: opsKeys.rates(companyId ?? '') }),
  });
}

export function useCompanyInvoices(companyId: string | undefined) {
  return useQuery({
    queryKey: opsKeys.invoices(companyId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<ContractorInvoiceView[]>(
        `/operations/companies/${companyId}/invoices`,
      );
      return data;
    },
    enabled: !!companyId,
  });
}

export function useCompanyInvoice(companyId: string | undefined, id: string | null) {
  return useQuery({
    queryKey: opsKeys.invoice(companyId ?? '', id ?? ''),
    queryFn: async () => {
      const { data } = await api.get<ContractorInvoiceView>(
        `/operations/companies/${companyId}/invoices/${id}`,
      );
      return data;
    },
    enabled: !!companyId && !!id,
  });
}

function useInvalidateInvoices(companyId: string | undefined) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: opsKeys.invoices(companyId ?? '') });
}

export function useDraftInvoice(companyId: string | undefined) {
  const invalidate = useInvalidateInvoices(companyId);
  return useMutation({
    mutationFn: async (body: {
      localGovernmentId: string;
      month: string;
      language?: 'en' | 'fr';
      notes?: string;
    }) => {
      const { data } = await api.post<ContractorInvoiceView>(
        `/operations/companies/${companyId}/invoices`,
        body,
      );
      return data;
    },
    onSuccess: invalidate,
  });
}

export type InvoiceAction = 'send' | 'paid' | 'void';

export function useMoveInvoice(companyId: string | undefined) {
  const invalidate = useInvalidateInvoices(companyId);
  return useMutation({
    mutationFn: async ({
      id,
      action,
      paidOn,
      paymentReference,
    }: {
      id: string;
      action: InvoiceAction;
      paidOn?: string;
      paymentReference?: string;
    }) => {
      const { data } = await api.post<ContractorInvoiceView>(
        `/operations/companies/${companyId}/invoices/${id}/${action}`,
        action === 'paid' ? { paidOn, paymentReference } : {},
      );
      return data;
    },
    onSuccess: invalidate,
  });
}

/** Save a Word or PDF of an invoice, from the contractor's side or the town's. */
export async function downloadInvoice(
  path: { companyId: string } | { localGovernmentId: string },
  invoice: Pick<ContractorInvoiceView, 'id' | 'number' | 'month' | 'language'>,
  format: 'docx' | 'pdf',
): Promise<void> {
  const base =
    'companyId' in path
      ? `/operations/companies/${path.companyId}`
      : `/operations/local-governments/${path.localGovernmentId}`;
  let res;
  try {
    res = await api.get(`${base}/invoices/${invoice.id}/document`, {
      params: { format },
      responseType: 'blob',
    });
  } catch (err) {
    // A refusal arrives as a Blob too; read it back into JSON so the error
    // carries its `code` (e.g. `pdf_unavailable`) like any other.
    const response = (err as { response?: { data?: unknown } }).response;
    if (response && response.data instanceof Blob) {
      try {
        response.data = JSON.parse(await response.data.text());
      } catch {
        // Not JSON; leave it.
      }
    }
    throw err;
  }
  const url = URL.createObjectURL(res.data as Blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    const stem = invoice.language === 'fr' ? 'facture' : 'invoice';
    const id = (invoice.number || `${invoice.month}-draft`).replace(/[^A-Za-z0-9-]/g, '');
    anchor.download = `${stem}-${id}.${format}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
